import React, { useState } from 'react';
import './enquirypage.css';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom'; // Import navigate
import { SEND_API } from '../../utils/APIs';
import { RECAPTCHA_SITEKEY } from '../../utils/config';

function Enquirypage() {
    const navigate = useNavigate();

    // 1. All necessary state variables
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        course: '',
        subject: 'Course enquiry',
        message: ''
    });

    const [errors, setErrors] = useState({});
    const [captchaVerified, setCaptchaVerified] = useState(false);
    const [responseMessage, setResponseMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    // 2. Handle Input Changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    // 3. Simple Form Validation
    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Name is required";
        if (!formData.email.trim()) newErrors.email = "Email is required";
        if (!formData.phone.trim()) newErrors.phone = "Phone is required";
        if (!formData.course) newErrors.course = "Please select a course";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // 4. Handle Captcha
    const handleCaptchaChange = (value) => {
        setCaptchaVerified(!!value);
    };

    // 5. Submit Logic
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        if (!captchaVerified) {
            setResponseMessage("Please verify the CAPTCHA.");
            return;
        }

        setIsLoading(true);
        setResponseMessage("");

        const formSend = new FormData();
        formSend.append("name", formData.name);
        formSend.append("email", formData.email);
        formSend.append("phone", formData.phone);
        formSend.append("course", formData.course);
        formSend.append("subject", formData.subject);
        formSend.append("message", formData.message);

        try {
            const response = await fetch(`${SEND_API}`, {
                method: "POST",
                mode: "cors",
                body: formSend,
            });

            if (!response.ok) {
                throw new Error(`HTTP Error! Status: ${response.status}`);
            }

            const result = await response.text();
            console.log("Server Raw Response:", result);

            if (result.toLowerCase().includes("success")) {
                setFormData({
                    name: "",
                    email: "",
                    phone: "",
                    course: "",
                    subject: 'Course enquiry',
                    message: "",

                });
                navigate("/thankyou"); // Ensure this path matches your App.js
            } else {
                setResponseMessage("Email not sent. Server rejected request.");
            }
        } catch (error) {
            console.error("Frontend Fetch Error Details:", error);
            setResponseMessage(`Connection failed (${error.message}).`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <section className="enquiry-section">
            <div className="decorative-pattern"></div>

            <div className="enquiry-container">
                <h2 className="enquiry-title">Enquiry Now</h2>

                <form onSubmit={handleSubmit} className="enquiry-form">
                    <div className="form-grid">
                        <div className="form-group">
                            <label htmlFor="name">Name</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                placeholder="Enter your Name"
                                value={formData.name}
                                onChange={handleChange}
                            />
                            {errors.name && <span className="error-text">{errors.name}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                placeholder="Enter your Email"
                                value={formData.email}
                                onChange={handleChange}
                            />
                            {errors.email && <span className="error-text">{errors.email}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="phone">Phone Number</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                placeholder="Phone Number"
                                value={formData.phone}
                                onChange={handleChange}
                                maxLength={10}
                            />
                            {errors.phone && <span className="error-text">{errors.phone}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="course">Course interested in</label>
                            <select
                                id="course"
                                name="course"
                                value={formData.course}
                                onChange={handleChange}
                            >
                                <option value="" disabled hidden>Course interested in *</option>
                                <option value="AI & Machine Learning">AI & Machine Learning</option>
                                <option value="Python Development">Python Development</option>
                                <option value="Java Full Stack">Java Full Stack</option>
                                <option value=".Net Framework">.Net Framework</option>
                                <option value="Java">Java</option>
                                <option value=".NET">.NET</option>
                                <option value="Python">Python</option>
                                <option value="C, C++">C, C++</option>
                                <option value="PHP">PHP</option>
                                <option value="Open stack">Open stack</option>
                                <option value="Kubernetes">Kubernetes</option>
                                <option value="DevOps">DevOps</option>
                                <option value="Data Science">Data Science</option>
                                <option value="Deep Learning">Deep Learning</option>
                                <option value="Machine Learning">Machine Learning</option>
                                <option value="AI - Artificial Intelligence">AI – Artificial Intelligence</option>
                                <option value="AWS">AWS</option>
                                <option value="AZURE">AZURE</option>
                                <option value="SALESFORCE">SALESFORCE (Developer / Administrator)</option>
                                <option value="Manual Testing">Manual Testing</option>
                                <option value="Selenium">Selenium</option>
                                <option value="JMeter">JMeter</option>
                                <option value="ETL Testing">ETL Testing</option>
                                <option value="Big Data Testing">Big Data Testing</option>
                                <option value="Load Runner testing">Load Runner testing</option>
                                <option value="CSS">CSS</option>
                                <option value="HTML">HTML</option>
                                <option value="Angular JS">Angular JS</option>
                                <option value="Nodejs">Nodejs</option>
                                <option value="UI/UX">UI/UX</option>
                                <option value="MYSQL">MYSQL</option>
                                <option value="MONGODB">MONGODB</option>
                                <option value="SQL SERVER DBA">SQL SERVER DBA</option>
                                <option value="CCNA">CCNA</option>
                                <option value="CCNP">CCNP</option>
                                <option value="A+N+">A+N+</option>
                                <option value="LINUX">LINUX</option>

                            </select>
                            {errors.course && <span className="error-text">{errors.course}</span>}
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="message">Message</label>
                        <textarea
                            id="message"
                            name="message"
                            rows="6"
                            placeholder="Enter your Message"
                            value={formData.message}
                            onChange={handleChange}
                        ></textarea>
                    </div>
                    <br></br>

                    <div className="captcha-wrapperr">
                        <ReCAPTCHA
                            sitekey={RECAPTCHA_SITEKEY }
                            onChange={handleCaptchaChange}
                        />
                    </div>

                    <div className="submit-container">
                        <button type="submit" className="details-btn" disabled={isLoading}>
                            {isLoading ? "Submitting..." : "Submit"}
                        </button>
                    </div>

                    {responseMessage && <p className="status-message">{responseMessage}</p>}
                </form>
            </div>
        </section>
    );
}

export default Enquirypage;